import React, { useState, useEffect, useCallback, useRef } from "react"
import { Editor } from "@monaco-editor/react"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import Tooltip from "aldoo-ra/Tooltip"
import { showNotification } from "aldoo-ra/Notification"
import { DocumentDuplicateIcon, SparklesIcon, XMarkIcon, CheckIcon } from "@heroicons/react/24/outline"

const HtmlEditor = ({ source, isOpen, onUpdate, onClose }) => {
  const [content, setContent] = useState("")
  const [originalContent, setOriginalContent] = useState("")
  const [hasChanges, setHasChanges] = useState(false)
  const editorRef = useRef(null)

  useEffect(() => {
    // Set the content based on the source prop when it changes
    if (source) {
      // Format the HTML by default when opening
      const formatted = formatHtml(source)
      setContent(formatted)
      setOriginalContent(formatted)
      setHasChanges(false)
    }
  }, [source])

  // Track changes in content
  useEffect(() => {
    setHasChanges(content !== originalContent)
  }, [content, originalContent])

  // Function to format HTML (similar to Cmd+Shift+F)
  const formatHtml = (html) => {
    if (!html) return ""
    
    try {
      // Simple HTML formatting - add indentation and line breaks
      let formatted = html
        .replace(/></g, '>\n<')
        .replace(/^\s+|\s+$/g, '')
      
      // Add proper indentation
      const lines = formatted.split('\n')
      let indentLevel = 0
      const indentSize = 2
      
      const formattedLines = lines.map(line => {
        const trimmed = line.trim()
        if (!trimmed) return ''
        
        // Decrease indent for closing tags
        if (trimmed.startsWith('</')) {
          indentLevel = Math.max(0, indentLevel - 1)
        }
        
        const indentedLine = ' '.repeat(indentLevel * indentSize) + trimmed
        
        // Increase indent for opening tags (but not self-closing)
        if (trimmed.startsWith('<') && 
            !trimmed.startsWith('</') && 
            !trimmed.endsWith('/>') &&
            !['br', 'hr', 'img', 'input', 'meta', 'link'].some(tag => 
              trimmed.startsWith(`<${tag}`)
            )) {
          indentLevel++
        }
        
        return indentedLine
      })
      
      return formattedLines.join('\n')
    } catch (error) {
      console.warn('Failed to format HTML:', error)
      return html
    }
  }

  // Function to handle the save action
  const handleSave = useCallback(() => {
    // Call the onUpdate function passed as a prop with the updated content
    if (onUpdate) {
      const result = content.replace(/<\/br>/g, "")
      onUpdate(result)
    }
    onClose()
  }, [content, onUpdate, onClose])

  // Function to handle close without saving
  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  // Function to copy HTML content to clipboard
  const handleCopyHtml = useCallback(() => {
    navigator.clipboard
      .writeText(content)
      .then(() => {
        showNotification({
          message: "HTML content copied to clipboard!",
          variant: "success"
        })
      })
      .catch((err) => {
        console.error("Failed to copy content:", err)
        showNotification({
          message: "Failed to copy HTML content",
          variant: "error"
        })
      })
  }, [content])

  // Function to format HTML using Monaco's built-in formatter
  const handleFormatHtml = useCallback(() => {
    if (editorRef.current) {
      const editor = editorRef.current
      editor.getAction('editor.action.formatDocument').run()
    }
  }, [])

  const handleEditorDidMount = (editor) => {
    editorRef.current = editor
    // Trigger formatting on mount
    setTimeout(() => {
      editor.getAction('editor.action.formatDocument').run()
    }, 100)
  }

  return (
    <Modal
      variant="fullScreen"
      isOpen={isOpen}
      onClose={handleClose}
      closeOnEscape={true}
      allowCloseBtn={false}
      className="overflow-hidden"
      styleOverrides={{
        overlay: "fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center",
        container: "w-full h-full bg-gray-900 text-white flex flex-col",
        contentWrapper: "flex-1 overflow-hidden",
        content: "p-0 h-full flex flex-col",
        header: "hidden"
      }}
    >
      {/* Header Section */}
      <div className="flex justify-between items-center p-4 border-b border-gray-700 bg-gray-800">
        <h3 className="text-xl font-bold">HTML Editor</h3>
        
        {/* Header Tools */}
        <div className="flex items-center gap-2">
          <Tooltip content="Copy HTML" position="bottom">
            <Button
              variant="admin_ghost"
              onClick={handleCopyHtml}
              className="flex items-center gap-2 px-3 py-2"
            >
              <DocumentDuplicateIcon className="w-4 h-4" />
              Copy HTML
            </Button>
          </Tooltip>

          <Tooltip content="Format HTML (Cmd+Shift+F)" position="bottom">
            <Button
              variant="admin_ghost"
              onClick={handleFormatHtml}
              className="flex items-center gap-2 px-3 py-2"
            >
              <SparklesIcon className="w-4 h-4" />
              Format
            </Button>
          </Tooltip>

          <Tooltip content="Close" position="bottom">
            <Button
              variant="admin_ghost"
              onClick={handleClose}
              className="flex items-center gap-2 px-3 py-2"
            >
              <XMarkIcon className="w-4 h-4" />
              Close
            </Button>
          </Tooltip>

          {hasChanges && (
            <Tooltip content="Update & Close" position="bottom">
              <Button
                variant="admin_primary"
                onClick={handleSave}
                className="flex items-center gap-2 px-4 py-2"
              >
                <CheckIcon className="w-4 h-4" />
                Update
              </Button>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Editor Section - Full height from toolbar to bottom with padding */}
      <div className="flex-1 p-4 overflow-hidden">
        <Editor
          width="100%"
          height="100%"
          language="html"
          value={content}
          onChange={(value) => setContent(value)}
          theme="vs-dark"
          onMount={handleEditorDidMount}
          options={{
            minimap: { enabled: true },
            scrollBeyondLastLine: false,
            lineNumbers: "on",
            wordWrap: "on",
            formatOnPaste: true,
            formatOnType: true,
            automaticLayout: true,
            fontSize: 14,
            tabSize: 2,
            insertSpaces: true,
            detectIndentation: false,
            folding: true,
            foldingStrategy: "indentation",
            showFoldingControls: "always",
            bracketPairColorization: { enabled: true },
          }}
        />
      </div>
    </Modal>
  )
}

export default HtmlEditor
